import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, Optional} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzSelectModule} from 'ng-zorro-antd/select';

export type MobileSortDirection = 'ascend' | 'descend' | null;

/**
 * The list screen's own sort, for a field no header cell sorts by. Every entity list sorts by id - it is
 * their default order - so a list whose table has no sortable columns can still offer "ID".
 */
export interface MobileListSortAdapter {
    current(): {field: string; direction: MobileSortDirection};
    sort(field: string, direction: MobileSortDirection): void;
}

export interface MobileSortData {
    table: HTMLElement;
    labelOf: (th: Element) => string;
    list?: MobileListSortAdapter;
}

/** A way to sort the list: a sortable header cell, or a field sorted through the list itself. */
interface SortOption {
    label: string;
    th?: HTMLElement;
    field?: string;
}

/**
 * "Sort by" for a list on a phone.
 *
 * Below 600px a list is a stack of cards and its header row is not rendered, so the column sort arrows -
 * the only way the desktop offers to sort - are gone. This gives them back as two controls: the column,
 * and the direction. It opens as a sheet from the sort icon beside the search box (MobileTableSortDirective).
 *
 * It does not sort anything itself. The columns are read from the table's own sortable header cells, and
 * choosing one drives that header cell exactly as a click on the desktop would: ng-zorro cycles it through
 * ascending, descending and off, and the screen's own (nzSortOrderChange) handler builds the request - so it
 * sends what the desktop sends, one column at a time, as every list here sorts. When the table has no ID
 * column, "ID" is added and sorted through the list's own sortBy, which is the order the list starts in.
 *
 * Styles: _mobile-kit.scss ("sort").
 */
@Component({
    selector: 'di-mobile-sort',
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule, NzSelectModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="di-msort" [class.is-sheet]="!!modalRef">
            <div class="di-mfilter-sheet-title" *ngIf="modalRef">{{ 'mobileKit.sortBy' | translate }}</div>
            <div class="di-msort-label" *ngIf="!modalRef">{{ 'mobileKit.sortBy' | translate }}</div>
            <nz-select
                class="di-msort-field"
                [ngModel]="field"
                (ngModelChange)="setField($event)"
                (nzOpenChange)="$event && refresh()"
                [nzAllowClear]="true"
                [nzPlaceHolder]="'mobileKit.defaultOrder' | translate">
                <nz-option *ngFor="let option of options" [nzValue]="option.label" [nzLabel]="option.label"></nz-option>
            </nz-select>
            <div class="di-msort-dir" role="group" [attr.aria-label]="'mobileKit.sortBy' | translate">
                <button type="button" [class.is-active]="direction === 'ascend'" [disabled]="!field" (click)="setDirection('ascend')">
                    <span class="di-mi">arrow_upward</span>{{ 'mobileKit.ascending' | translate }}
                </button>
                <button type="button" [class.is-active]="direction === 'descend'" [disabled]="!field" (click)="setDirection('descend')">
                    <span class="di-mi">arrow_downward</span>{{ 'mobileKit.descending' | translate }}
                </button>
            </div>
            <button type="button" class="di-mbtn is-primary is-block di-msort-done" *ngIf="modalRef" (click)="modalRef.close()">
                {{ 'mobileKit.done' | translate }}
            </button>
        </div>
    `
})
export class DiMobileSortComponent implements OnInit {
    /** The nz-table whose sortable header cells this sorts by. */
    @Input() table?: HTMLElement;
    /** How a header cell reads to a person; the same function the card labels use. */
    @Input() labelOf: (th: Element) => string = (th) => (th.textContent || '').trim();
    /** The list itself, for sorting by id when no header cell does. */
    @Input() list?: MobileListSortAdapter;

    options: SortOption[] = [];
    field: string | null = null;
    direction: MobileSortDirection = null;

    /** ng-zorro's default cycle; no screen here sets nzSortDirections. */
    private static readonly CYCLE: MobileSortDirection[] = ['ascend', 'descend', null];

    constructor(
        private readonly _cdr: ChangeDetectorRef,
        @Optional() private readonly _translate?: TranslateService,
        @Optional() public readonly modalRef?: NzModalRef,
        @Optional() @Inject(NZ_MODAL_DATA) data?: MobileSortData
    ) {
        if (data) {
            this.table = data.table;
            this.labelOf = data.labelOf;
            this.list = data.list;
        }
    }

    /** The table's own sortable header cells - not those of a table nested inside one of its rows. */
    static sortableHeaders(table: HTMLElement): HTMLElement[] {
        return Array.from(table.querySelectorAll<HTMLElement>('th.ant-table-column-has-sorters')).filter(
            (th) => th.closest('nz-table, table[diMobileCards]') === table
        );
    }

    static directionOf(th: HTMLElement): MobileSortDirection {
        if (th.querySelector('.ant-table-column-sorter-up.active')) {
            return 'ascend';
        }
        if (th.querySelector('.ant-table-column-sorter-down.active')) {
            return 'descend';
        }
        return null;
    }

    ngOnInit(): void {
        this.refresh();
    }

    /** Re-read the columns and the current sort: Display Settings can add or remove a column at any time. */
    refresh(): void {
        const columns: SortOption[] = this.table
            ? DiMobileSortComponent.sortableHeaders(this.table)
                  .map((th) => ({th, label: this.labelOf(th)}))
                  .filter((column) => !!column.label)
            : [];
        const hasIdColumn = columns.some((column) => column.label.trim().toLowerCase() === 'id');
        if (this.list && !hasIdColumn) {
            columns.unshift({label: this._translate?.instant('mobileKit.id') ?? 'ID', field: 'id'});
        }
        this.options = columns;

        const sortedColumn = columns.find((column) => column.th && DiMobileSortComponent.directionOf(column.th));
        if (sortedColumn) {
            this.field = sortedColumn.label;
            this.direction = DiMobileSortComponent.directionOf(sortedColumn.th);
        } else {
            // No header cell is sorted: the list is in its own order, which for an entity list is by id.
            const current = this.list?.current();
            const byField = current && columns.find((column) => column.field && column.field === current.field);
            this.field = byField?.label ?? null;
            this.direction = byField ? current.direction : null;
        }
        this._cdr.markForCheck();
    }

    setField(label: string | null): void {
        if (!label) {
            // Cleared: switch off whichever column is sorted, which hands the list back to its default order.
            const sorted = this.options.find((option) => option.th && DiMobileSortComponent.directionOf(option.th));
            if (sorted?.th) {
                this._drive(sorted.th, null);
            }
            this.field = null;
            this.direction = null;
            return;
        }
        this.field = label;
        this.setDirection(this.direction ?? 'ascend');
    }

    setDirection(direction: MobileSortDirection): void {
        const option = this.options.find((o) => o.label === this.field);
        if (!option || !direction) {
            return;
        }
        if (option.th) {
            this._drive(option.th, direction);
        } else if (option.field && this.list) {
            // Moving off a header-sorted column first, so its arrow does not claim a sort it no longer has.
            const sorted = this.options.find((o) => o.th && DiMobileSortComponent.directionOf(o.th));
            if (sorted?.th) {
                this._drive(sorted.th, null);
            }
            this.list.sort(option.field, direction);
        }
        this.direction = direction;
    }

    /**
     * Click the header cell until it reaches the wanted direction. The number of clicks comes from where it
     * starts in ng-zorro's cycle rather than from re-reading the DOM between clicks: its state changes on the
     * click, but the arrow classes only change on the next change detection.
     */
    private _drive(th: HTMLElement, target: MobileSortDirection): void {
        const cycle = DiMobileSortComponent.CYCLE;
        const from = cycle.indexOf(DiMobileSortComponent.directionOf(th));
        const to = cycle.indexOf(target);
        const clicks = (to - from + cycle.length) % cycle.length;
        for (let i = 0; i < clicks; i++) {
            th.click();
        }
    }
}
